import { z } from "zod"
import { type DEXStatsType, DEXStatsSchema } from "@tutellus/dappcomposer-contracts-ifaces"
import { CurrencyAmount } from "../../_kernel/CurrencyAmount"

const ProjectStatsInputSchema = z.object({
  tvl: z.array(z.instanceof(CurrencyAmount)),
  volume: z.array(z.instanceof(CurrencyAmount)),
  tx: z.number().int(),
  inventives: z.array(z.instanceof(CurrencyAmount)),
  fees: z.array(z.instanceof(CurrencyAmount)),
  income: z.array(z.instanceof(CurrencyAmount)),
})

export class ProjectStats {
  constructor(
    private readonly _tvl: CurrencyAmount[],
    private readonly _volume: CurrencyAmount[],
    private readonly _tx: number,
    private readonly _inventives: CurrencyAmount[],
    private readonly _fees: CurrencyAmount[],
    private readonly _income: CurrencyAmount[],
  ) {}

  static create({ tvl, volume, tx, inventives, fees, income }: z.infer<typeof ProjectStatsInputSchema>) {
    const { success, error } = ProjectStatsInputSchema.safeParse({ tvl, volume, tx, inventives, fees, income })
    if (!success) throw new Error(error.message)
    return new ProjectStats(tvl, volume, tx, inventives, fees, income)
  }

  static createFromAPI({ tvl, volume, tx, bribes, fees, income }: DEXStatsType) {
    const { success, error } = DEXStatsSchema.safeParse({ tvl, volume, tx, bribes, fees, income })
    if (!success) throw new Error(error.message)

    return new ProjectStats(
      tvl.map(CurrencyAmount.create),
      volume.map(CurrencyAmount.create),
      tx,
      bribes.map(CurrencyAmount.create),
      fees.map(CurrencyAmount.create),
      income.map(CurrencyAmount.create),
    )
  }

  get tvl() { return this._tvl } // prettier-ignore
  get volume() { return this._volume } // prettier-ignore
  get tx() { return this._tx } // prettier-ignore
  get inventives() { return this._inventives } // prettier-ignore
  get fees() { return this._fees } // prettier-ignore
  get income() { return this._income } // prettier-ignore

  serialize() {
    return {
      tvl: this._tvl.map((c) => c.serialize()),
      volume: this._volume.map((c) => c.serialize()),
      tx: this._tx,
      inventives: this._inventives.map((c) => c.serialize()),
      fees: this._fees.map((c) => c.serialize()),
      income: this._income.map((c) => c.serialize()),
    }
  }
}

export type ProjectStatsSerialized = ReturnType<ProjectStats["serialize"]>
